import React from 'react';
import Box from '@mui/material/Box';
import { gql } from '@yi/core';

import { graphql } from 'gatsby';
import { utilities, analytics } from '~/common';
import { black54 } from '~/styles';

import { Avatar } from '../../ClassIconComponents';
import { ContentCardMenu } from '../../ContentCardMenu';
import { CurrentUser } from '../../CurrentUser';
import { ArticlesIcon } from '../../icons';
import { LazyLoadWrapper } from '../../LazyLoadWrapper';
import { BaseCard, BaseCardProps } from '../bases';
import {
  Author,
  AuthorNameDark,
  BookmarkIconButton,
  ButtonWrapper,
  CardContentDescriptions,
  CardContentHeader,
  CardContentTitle,
  LevelTitle,
  ArticleGradientOverlay,
  ArticleCardContent,
} from '../elements';
import { NewChip } from '../NewChip';

const { truncate } = utilities;
const { sendCardKebabMenuClicked } = analytics;

type Item = gql.typesClient.Fragment_ArticleCard;
interface Props extends Omit<Partial<BaseCardProps>, 'onClick'> {
  item: Item;
  noHover?: boolean;
  onClick?(i: Item): void;
}

export class ArticleCard extends React.Component<Props> {
  static dimension = {
    width: 200,
    height: 320,
  };

  static graphql = graphql`
    fragment Fragment_ArticleCard on YI_Article {
      id
      title
      localized_path
      snippet
      entry_date {
        yyyymmdd
      }
      teacher {
        id
        full_name
        image(params: { width: 50, format: webp }) {
          processed_url
        }
      }
      no_text_image(params: { width: 350, format: webp }) {
        processed_url
      }
      topic {
        id
        name
      }
    }
  `;

  renderTopic = () => {
    const {
      item: { topic },
    } = this.props;
    return (
      <div>
        <span style={{ fontSize: '0.65rem' }}>{topic.name}</span>
      </div>
    );
  };

  render() {
    const { item, noHover = true, onClick = () => undefined, ...props } = this.props;
    const { id, title, localized_path, snippet, teacher, no_text_image, topic, entry_date } = item;
    const truncatedTitle = truncate(title, 32, '...');
    const truncatedSnippet = truncate(snippet, 68, '...');
    const imageHeight = 166;
    const { dimension } = ArticleCard;

    return (
      <CurrentUser>
        {({ isLoggedIn }) => (
          <BaseCard
            onClick={() => {
              onClick(item);
            }}
            dimension={dimension}
            hoverable={!noHover}
            url={localized_path}
            thumb={no_text_image?.processed_url}
            imageHeight={imageHeight}
            bgImageFull={false}
            renderImageOverlay={() => <ArticleGradientOverlay />}
            renderContent={() => (
              <ArticleCardContent>
                <CardContentHeader>
                  <ArticlesIcon style={{ height: '16px', width: '16px', color: black54 }} />
                  <LevelTitle>{topic.name}</LevelTitle>
                </CardContentHeader>
                <CardContentTitle>{truncatedTitle}</CardContentTitle>
                <CardContentDescriptions>{truncatedSnippet}</CardContentDescriptions>
              </ArticleCardContent>
            )}
            renderUpperLeft={() => <NewChip entryDate={entry_date} />}
            renderUpperRight={() =>
              isLoggedIn && (
                <ButtonWrapper>
                  <BookmarkIconButton>
                    <ContentCardMenu
                      contentId={id}
                      contentTitle={title}
                      onClick={() => sendCardKebabMenuClicked({ title, cardType: 'article' })}
                      cardType="article"
                    />
                  </BookmarkIconButton>
                </ButtonWrapper>
              )
            }
            renderLowerLeft={() => (
              <Box padding="0 0 8px">
                <Author>
                  <LazyLoadWrapper enabled={props.lazyLoad} width={32} height={24}>
                    <Avatar
                      imgProps={{ role: 'presentation' }}
                      src={teacher?.image?.processed_url}
                    />
                  </LazyLoadWrapper>
                  <AuthorNameDark>{teacher.full_name}</AuthorNameDark>
                </Author>
              </Box>
            )}
            {...props}
          />
        )}
      </CurrentUser>
    );
  }
}
